/**
 * API の失敗を表す唯一の例外型（サーバの `{ error: { code, message, details } }` を正規化したもの）。
 * status 0 = ネットワーク到達不能（05 §5.14 #2）。
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details: unknown = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  get isNetwork(): boolean {
    return this.status === 0;
  }
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
  get isForbidden(): boolean {
    return this.status === 403;
  }
  get isNotFound(): boolean {
    return this.status === 404;
  }

  static network(): ApiError {
    return new ApiError(0, 'NETWORK', 'サーバにつながりません。しばらくしてからもう一度お試しください');
  }

  /** サーバのバリデーション詳細（`[{ path, message }]`）を欄ごとの辞書に */
  fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    if (Array.isArray(this.details)) {
      for (const d of this.details as Array<{ path?: unknown; message?: unknown }>) {
        if (typeof d?.path === 'string' && typeof d?.message === 'string' && !(d.path in out)) out[d.path] = d.message;
      }
    }
    return out;
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError || (typeof e === 'object' && e !== null && (e as { name?: string }).name === 'ApiError');
}

/** 利用者に見せる文言。原因ごとに違う文言にする（05 §5.14「エラーが発生しました」は禁止） */
export function errorMessage(e: unknown, fallback = '処理できませんでした。もう一度お試しください'): string {
  if (isApiError(e)) {
    if (e.isNetwork) return e.message;
    if (e.status === 401) return 'もう一度ログインしてください';
    if (e.status === 403) return e.message || 'この操作を行う権限がありません';
    if (e.status === 404) return e.message || '対象が見つかりません';
    if (e.status === 429) return 'しばらく待ってからもう一度お試しください';
    if (e.status >= 500) return 'サーバ内部でエラーが発生しました。しばらくしてからもう一度お試しください';
    return e.message || fallback;
  }
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}
